export type ChecklistCsvRow = {
  category: string;
  title: string;
  description: string;
};

const HEADER_CELL = /^(category|categories|item|items|title|description|name|example)$/i;

/** Split one CSV line, keeping commas that sit inside quotes. */
export function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
}

/**
 * Company checklist paste.
 * Three columns: category, item, description.
 * Two columns: item, description (category General).
 * One column: item title.
 */
export function parseQcChecklistCsv(text: string): ChecklistCsvRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (lines.length === 0) return [];
  const first = splitCsvLine(lines[0]).map((cell) => cell.trim());
  const hasHeader = first.some((cell) => HEADER_CELL.test(cell));
  const body = hasHeader ? lines.slice(1) : lines;
  const rows: ChecklistCsvRow[] = [];
  for (const line of body) {
    const cells = splitCsvLine(line).map((cell) => cell.trim());
    if (cells.every((cell) => cell.length === 0)) continue;
    if (cells.length >= 3) {
      rows.push({
        category: (cells[0] || "General").toUpperCase(),
        title: cells[1] ?? "",
        description: cells.slice(2).join(", "),
      });
    } else if (cells.length === 2) {
      rows.push({ category: "GENERAL", title: cells[0] ?? "", description: cells[1] ?? "" });
    } else {
      rows.push({ category: "GENERAL", title: cells[0] ?? "", description: "" });
    }
  }
  return rows.filter((row) => row.title.length > 0 || row.category.length > 0);
}

function cleanSpokenName(value: string): string {
  return value
    .replace(/\betc\.?\b/gi, "")
    .replace(/^[\s,"']+|[\s,"'.]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function splitSpokenItems(value: string): string[] {
  return value
    .split(/,|\band\b/i)
    .map(cleanSpokenName)
    .filter((part) => part.length > 0);
}

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

const CATEGORY_LIST_CUE =
  /(?:^|[\n,])\s*category\s+(?!(?:number|no\.?|#)?\s*\d+\b)([^:,\n]+?)\s*[:,]\s*/gi;

/**
 * "Category FLOOR, gap check, runner spacing, Category WALLS, studs plumb".
 * The word Category names the section. Each comma after it is its own item.
 */
export function parseQcChecklistCategoryList(
  text: string,
  existingCategories: readonly string[] = [],
): ChecklistCsvRow[] {
  const raw = text.trim();
  if (!raw) return [];
  const matches = [...raw.matchAll(CATEGORY_LIST_CUE)];
  if (matches.length === 0) return [];
  const known = existingCategories
    .map((name) => name.trim().replace(/\s+/g, " ").toUpperCase())
    .filter((name) => name.length > 0);
  const rows: ChecklistCsvRow[] = [];
  for (let i = 0; i < matches.length; i += 1) {
    const category = resolveChecklistCategory(matches[i][1] ?? "", known);
    if (!category) continue;
    if (!known.includes(category)) known.push(category);
    const start = (matches[i].index ?? 0) + matches[i][0].length;
    const end = matches[i + 1]?.index ?? raw.length;
    const items = raw
      .slice(start, end)
      .split(",")
      .map((part) => part.replace(/\s+/g, " ").trim())
      .filter((part) => part.length > 0);
    if (items.length === 0) rows.push({ category, title: "", description: "" });
    else for (const title of items) rows.push({ category, title, description: "" });
  }
  return rows;
}

function speechCue(): RegExp {
  return /\b(?:add\s+categories|new\s+category|next\s+category|category(?:\s+(?:number|no\.?|#))?\s*\d+|new\s+item|next\s+item|item(?:\s+(?:number|no\.?|#))?\s*\d+|add)\b/gi;
}

function cueKind(cue: string): "category" | "item" {
  return /categor/i.test(cue) ? "category" : "item";
}

/** Match a spoken category to one already on the checklist, or keep the spoken name in capitals. */
export function resolveChecklistCategory(spoken: string, existing: readonly string[]): string {
  const key = spoken
    .trim()
    .replace(/^(?:the|a|an)\s+/i, "")
    .replace(/\s+/g, " ")
    .toUpperCase();
  if (!key) return "";
  const caps = existing
    .map((name) => name.trim().replace(/\s+/g, " ").toUpperCase())
    .filter((name) => name.length > 0);
  const exact = caps.find((name) => name === key);
  if (exact) return exact;
  return key;
}

function categoryNamesFromPayload(payload: string): string[] {
  const stripped = payload.trim().replace(/^(?:for|called|named)\s+/i, "");
  const cut = stripped.split(/\bthen\b|\bunder\b/i)[0] ?? "";
  return splitSpokenItems(cut)
    .map((name) => name.replace(/^(?:the|a|an)\s+/i, "").toUpperCase())
    .filter((name) => name.length > 0);
}

function itemRowsFromPayload(
  payload: string,
  fallbackCategory: string,
  resolve: (name: string) => string,
): ChecklistCsvRow[] {
  const toMatch = payload.match(/^(.*?)\s+to\s+(.+)$/i);
  const titleSource = toMatch ? toMatch[1] ?? "" : payload.split(/,|\bthen\b/i)[0] ?? "";
  const titles = splitSpokenItems(titleSource).filter(
    (title) => title.length > 1 && !/^(?:a|an|the|to|for|then|under|those|categories)$/i.test(title),
  );
  if (titles.length === 0) return [];
  const dests = toMatch
    ? splitSpokenItems(toMatch[2] ?? "")
        .map((name) => resolve(name))
        .filter((name) => name.length > 0 && !/^(?:THEN|UNDER|THOSE|CATEGORIES)$/.test(name))
    : [];
  const targets = dests.length > 0 ? dests : fallbackCategory ? [fallbackCategory] : [];
  const rows: ChecklistCsvRow[] = [];
  for (const title of titles) {
    for (const category of targets) rows.push({ category, title, description: "" });
  }
  return rows;
}

/**
 * One dictation can name several categories and the items under them.
 * Category cues: "category 1", "new category", "next category", "add categories".
 * Item cues: "item 1", "new item", "next item", or "add … to …".
 * Categories are stored in capitals. Item text keeps the spoken or typed case.
 */
export function parseQcChecklistSpeech(
  text: string,
  existingCategories: readonly string[] = [],
): ChecklistCsvRow[] {
  const listed = parseQcChecklistCategoryList(text, existingCategories);
  if (listed.length > 0) return listed;
  const raw = text.trim();
  if (!raw) return [];
  const hits: Array<{ kind: "category" | "item"; start: number; end: number }> = [];
  for (const match of raw.matchAll(speechCue())) {
    const cue = match[0] ?? "";
    const start = match.index ?? 0;
    hits.push({ kind: cueKind(cue), start, end: start + cue.length });
  }
  if (hits.length === 0) return [];
  const known = existingCategories
    .map((name) => name.trim().replace(/\s+/g, " ").toUpperCase())
    .filter((name) => name.length > 0);
  const created: string[] = [];
  const pool = () => [...known, ...created];
  const resolve = (name: string) => resolveChecklistCategory(name, pool());
  const itemRows: ChecklistCsvRow[] = [];
  let lastCategory = "";
  for (let i = 0; i < hits.length; i += 1) {
    const hit = hits[i];
    const payload = raw.slice(hit.end, hits[i + 1]?.start ?? raw.length);
    if (hit.kind === "category") {
      for (const name of categoryNamesFromPayload(payload)) {
        const resolved = resolve(name);
        if (!resolved) continue;
        lastCategory = resolved;
        if (!known.includes(resolved) && !created.includes(resolved)) created.push(resolved);
      }
    } else {
      const rows = itemRowsFromPayload(payload, lastCategory, resolve);
      for (const row of rows) {
        if (!known.includes(row.category) && !created.includes(row.category)) created.push(row.category);
        lastCategory = row.category;
      }
      itemRows.push(...rows);
    }
  }
  return [
    ...created.map((category) => ({ category, title: "", description: "" })),
    ...itemRows,
  ];
}

export function formatQcChecklistCsv(rows: readonly ChecklistCsvRow[]): string {
  return rows
    .map((row) => [row.category, row.title, row.description].map(escapeCsvCell).join(", "))
    .join("\n");
}

const TITLE_STOP = new Set([
  "the",
  "a",
  "an",
  "is",
  "are",
  "was",
  "were",
  "of",
  "and",
  "to",
  "for",
  "on",
  "in",
  "it",
  "this",
  "that",
  "with",
  "be",
  "or",
]);

/** Pick the checklist item whose title words all appear in the spoken failure. */
export function matchChecklistItem(
  description: string,
  items: readonly { id: string; title: string }[],
): string | null {
  const desc = description.toLowerCase();
  let best: { id: string; score: number } | null = null;
  for (const item of items) {
    const tokens = item.title
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length >= 3 && !TITLE_STOP.has(token));
    if (tokens.length === 0) continue;
    if (!tokens.every((token) => desc.includes(token))) continue;
    const score = tokens.join(" ").length;
    if (!best || score > best.score) best = { id: item.id, score };
  }
  return best?.id ?? null;
}
